import {
  ASSESSMENT_STATUSES,
  CONVERSATION_SHOW,
  CONVERSATION_SORT,
  CONVERSATIONS_MAX,
  MESSAGE_MAX,
  type AssessmentStatus,
  type Conversation,
  type ConversationShow,
  type ConversationSort,
  type Message,
  type MessageSide,
  type SystemEvent,
} from '@cyberscore/contracts';
import {
  ArrowDown,
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  Gavel,
  Lock,
  Mail,
  MessageSquare,
  MessagesSquare,
  PencilLine,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Undo2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { initialsOf, nameColour } from '../../lib/avatar';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { useAssessment, useQuestions } from '../assessments/api';
import { STATUS_LABEL } from '../assessments/labels';
import { useConversationList, useConversations, useMarkRead, usePostMessage, useThread } from './api';

const SIDE: Record<MessageSide, string> = { insurer: 'Insurer', broker: 'Broker', business: 'Business' };

const EVENT_ICON: Record<SystemEvent, LucideIcon> = {
  changes_requested: PencilLine,
  change_replied: Undo2,
  changes_returned: RotateCcw,
  decided: Gavel,
  condition_evidenced: FileCheck2,
  condition_reviewed: CheckCircle2,
};

/** Two entries by the same person within this long read as one block, as in Slack. */
const GROUP_MS = 5 * 60_000;

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-MY', { hour: 'numeric', minute: '2-digit' });
const dayKey = (iso: string) => new Date(iso).toDateString();

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-MY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** When a conversation was last active, as its row in the list shows it. */
function shortWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return time(iso);
  if (now.getTime() - d.getTime() < 6 * 86_400_000)
    return d.toLocaleDateString('en-MY', { weekday: 'short' });
  return d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' });
}

/** What someone is writing, kept per conversation (and question) while they look at another one. */
const drafts = new Map<string, string>();

type When = 'any' | 'today' | '7d' | '30d' | 'custom';
interface Filters {
  q: string;
  client: string;
  status: AssessmentStatus | '';
  when: When;
  fromDay: string;
  toDay: string;
  show: ConversationShow;
  sort: ConversationSort;
  /** The period as moments, worked out when it is picked, so "today" is the viewer's today. */
  range: { from?: string; to?: string };
}
const NO_FILTERS: Filters = { q: '', client: '', status: '', when: 'any', fromDay: '', toDay: '', show: 'all', sort: 'recent', range: {} }; // prettier-ignore
/** The filters stay as they were while someone looks at a conversation or another page. */
let kept: Filters = NO_FILTERS;

const WHEN: Record<When, string> = {
  any: 'Any time',
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  custom: 'Between dates…',
};
const SHOW: Record<ConversationShow, string> = { all: 'All', unread: 'Unread', waiting: 'Waiting on reply' };
const SORT: Record<ConversationSort, string> = {
  recent: 'Latest activity',
  oldest: 'Oldest activity',
  unread: 'Most unread',
  client: 'Client, A to Z',
};

/** The moments a period covers, from the start of the viewer's first day to the start of the day after the last. */
function period(f: Filters): Filters['range'] {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const daysAgo = (n: number) => {
    const d = startOf(new Date());
    d.setDate(d.getDate() - n);
    return d.toISOString();
  };
  switch (f.when) {
    case 'any':
      return {};
    case 'today':
      return { from: daysAgo(0) };
    case '7d':
      return { from: daysAgo(6) };
    case '30d':
      return { from: daysAgo(29) };
    case 'custom': {
      const to = f.toDay ? new Date(`${f.toDay}T00:00`) : null;
      to?.setDate(to.getDate() + 1);
      return {
        ...(f.fromDay ? { from: new Date(`${f.fromDay}T00:00`).toISOString() } : {}),
        ...(to ? { to: to.toISOString() } : {}),
      };
    }
  }
}

/**
 * Messages (task 65, decision 0039), laid out like Slack: every conversation the person takes part in down
 * the side, newest first with what is unread, and the open one beside it. A conversation is an
 * assessment's thread between the insurer, the broker and the business: messages, comments on single
 * questions, the insurer's internal notes, and what the platform records when changes are asked for,
 * answered or a decision is made. It refreshes every few seconds, and what you send shows at once. The
 * list can be searched (what was written, and who it is about), narrowed to a client, a period, an
 * assessment's status, what is unread or what is waiting on a reply, and sorted.
 */
export function MessagesPage({ id }: { id: string | null }) {
  const [f, setF] = useState<Filters>(kept);
  const [q, setQ] = useState(kept.q);
  const [panel, setPanel] = useState(false);
  const set = (patch: Partial<Filters>) =>
    setF((old) => {
      const next = { ...old, ...patch };
      return (kept = { ...next, range: period(next) });
    });
  // A search waits for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => set({ q: q.trim() }), 300);
    return () => clearTimeout(t);
  }, [q]);

  const everything = useConversations();
  const list = useConversationList({
    ...(f.client ? { client: f.client } : {}),
    ...(f.q ? { q: f.q } : {}),
    ...(f.status ? { status: f.status } : {}),
    ...f.range,
    show: f.show,
    sort: f.sort,
  });
  const open = useAssessment(id ?? '', { enabled: !!id });
  // The clients to pick from: everyone this person has a conversation with.
  const clients = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of everything.data ?? [])
      if (!seen.has(c.customer_key)) seen.set(c.customer_key, c.customer_name);
    return [...seen].sort((a, b) => a[1].localeCompare(b[1]));
  }, [everything.data]);
  const narrowed = !!(f.client || f.status || f.when !== 'any');
  const filtered = narrowed || !!f.q || f.show !== 'all';
  const extra = [f.client, f.status, f.when !== 'any', f.sort !== 'recent'].filter(Boolean).length;

  // A conversation with nothing in it yet (opened from its assessment) still has its place in the list.
  const rows = useMemo<Row[]>(() => {
    const all = list.data ?? [];
    const here = !filtered && id && open.data && !all.some((c) => c.assessment_id === id) ? [{ assessment_id: id, title: open.data.title, customer_name: open.data.customer_name, last: null, hit: null, unread: 0, waiting: false }] : []; // prettier-ignore
    return [...here, ...all];
  }, [list.data, id, open.data, filtered]);

  const clear = () => {
    setQ('');
    setF((kept = { ...NO_FILTERS }));
  };

  return (
    <div className="sl" data-open={id ? 'conversation' : 'list'}>
      <aside className="sl-side" aria-label="Conversations">
        <div className="sl-side-head">
          <h1>
            <MessagesSquare size={20} /> Messages
          </h1>
          <button type="button" className={`btn btn-sm btn-ghost sl-filter-btn${panel || extra ? ' on' : ''}`} aria-expanded={panel} aria-controls="sl-filters" onClick={() => setPanel((p) => !p)}>
            <SlidersHorizontal size={14} /> Filters
            {extra > 0 && <span className="sl-filter-count">{extra}</span>}
          </button>
        </div>
        <div className="search sl-find">
          <Search size={15} />
          <input aria-label="Search messages" placeholder="Search messages and clients" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && (
            <button type="button" className="sl-find-clear" aria-label="Clear the search" onClick={() => setQ('')}>
              <X size={13} />
            </button>
          )}
        </div>
        <div className="sl-show" role="radiogroup" aria-label="Show">
          {CONVERSATION_SHOW.map((v) => (
            <button key={v} type="button" role="radio" aria-checked={f.show === v} onClick={() => set({ show: v })}>
              {SHOW[v]}
            </button>
          ))}
        </div>
        {panel && (
          <div className="sl-filters" id="sl-filters">
            {clients.length > 1 && (
              <label>
                <span>Client</span>
                <select className="input" value={f.client} onChange={(e) => set({ client: e.target.value })}>
                  <option value="">All clients</option>
                  {clients.map(([key, name]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              <span>Assessment status</span>
              <select className="input" value={f.status} onChange={(e) => set({ status: e.target.value as Filters['status'] })}>
                <option value="">Any status</option>
                {ASSESSMENT_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {STATUS_LABEL[st]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Date</span>
              <select className="input" value={f.when} onChange={(e) => set({ when: e.target.value as When })}>
                {(Object.keys(WHEN) as When[]).map((w) => (
                  <option key={w} value={w}>
                    {WHEN[w]}
                  </option>
                ))}
              </select>
            </label>
            {f.when === 'custom' && (
              <div className="sl-days">
                <label>
                  <span>From</span>
                  <input type="date" className="input" value={f.fromDay} max={f.toDay || undefined} onChange={(e) => set({ fromDay: e.target.value })} />
                </label>
                <label>
                  <span>To</span>
                  <input type="date" className="input" value={f.toDay} min={f.fromDay || undefined} onChange={(e) => set({ toDay: e.target.value })} />
                </label>
              </div>
            )}
            <label>
              <span>Sort by</span>
              <select className="input" value={f.sort} onChange={(e) => set({ sort: e.target.value as ConversationSort })}>
                {CONVERSATION_SORT.map((v) => (
                  <option key={v} value={v}>
                    {SORT[v]}
                  </option>
                ))}
              </select>
            </label>
            {(extra > 0 || f.q || f.show !== 'all') && (
              <button type="button" className="linklike small" onClick={clear}>
                Clear all filters
              </button>
            )}
          </div>
        )}
        {list.isPending ? (
          <div className="sl-side-empty">Loading…</div>
        ) : list.isError ? (
          <div className="sl-side-empty">Could not load your conversations. {list.error.message}</div>
        ) : rows.length === 0 ? (
          <div className="sl-side-empty">
            {filtered ? (
              <>
                No conversation matches.{' '}
                <button type="button" className="linklike" onClick={clear}>
                  Clear the filters
                </button>
              </>
            ) : (
              'No conversations yet. Each assessment has one; it starts with the first message.'
            )}
          </div>
        ) : (
          <>
            {filtered && (
              <div className="sl-found" aria-live="polite">
                {rows.length === CONVERSATIONS_MAX ? `The first ${rows.length}` : rows.length} {rows.length === 1 ? 'conversation' : 'conversations'}
              </div>
            )}
            <ul className={`sl-convs${list.isPlaceholderData ? ' stale' : ''}`}>
              {rows.map((c) => (
                <li key={c.assessment_id}>
                  <ConversationRow c={c} on={c.assessment_id === id} q={f.q} />
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
      <section className="sl-main">
        {id ? (
          <Thread key={id} id={id} />
        ) : (
          <div className="sl-pick">
            <MessagesSquare size={44} />
            <h2>Pick a conversation</h2>
            <p>Every assessment has one conversation between the insurer, the broker and the business.</p>
          </div>
        )}
      </section>
    </div>
  ); // prettier-ignore
}

type Row = Pick<Conversation, 'assessment_id' | 'title' | 'customer_name' | 'unread' | 'waiting' | 'hit'> & {
  last: Conversation['last'] | null;
};

function ConversationRow({ c, on, q }: { c: Row; on: boolean; q: string }) {
  const last = c.last;
  // With a search or dates, the entry that matched; otherwise the latest.
  const shown =
    c.hit ?? (last && { body: last.body, author: last.kind === 'system' ? null : last.author, at: last.at });
  const preview = shown
    ? `${shown.author ? `${shown.author.split(/\s+/)[0]}: ` : ''}${shown.body}`
    : 'No messages yet';
  return (
    <a className={`sl-conv${c.unread > 0 ? ' unread' : ''}`} href={href.messages(c.assessment_id)} aria-current={on ? 'page' : undefined}>
      <Avatar name={c.customer_name} square />
      <span className="sl-conv-text">
        <span className="sl-conv-top">
          <b>{c.customer_name}</b>
          {shown && <time dateTime={shown.at}>{shortWhen(shown.at)}</time>}
        </span>
        <span className="sl-conv-title">
          {c.waiting && <span className="sl-waiting">Waiting on you · </span>}
          {c.title}
        </span>
        <span className="sl-conv-last">
          {!c.hit && last?.internal && <Lock size={11} />} <Marked text={preview} q={c.hit ? q : ''} />
        </span>
      </span>
      {c.unread > 0 && (
        <span className="sl-badge" aria-label={`${c.unread} unread`}>
          {c.unread > 99 ? '99+' : c.unread}
        </span>
      )}
    </a>
  ); // prettier-ignore
}

/** The searched words stand out; a match far into a long message is brought into view. */
function Marked({ text, q }: { text: string; q: string }) {
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  const from = at > 40 ? at - 20 : 0;
  return (
    <>
      {from > 0 && '…'}
      {text.slice(from, at)}
      <mark>{text.slice(at, at + q.length)}</mark>
      {text.slice(at + q.length)}
    </>
  );
}

function Avatar({
  name,
  square = false,
  children,
}: {
  name: string;
  square?: boolean;
  children?: ReactNode;
}) {
  return (
    <span className={`sl-av${square ? ' square' : ''}`} style={children ? undefined : { background: nameColour(name) }} aria-hidden>
      {children ?? initialsOf(name)}
    </span>
  ); // prettier-ignore
}

interface Pending {
  key: string;
  body: string;
  question: string | null;
  internal: boolean;
  failed: boolean;
  at: string;
}

/** One conversation: its header, the messages, and the box to write in, which stays at the bottom. */
function Thread({ id }: { id: string }) {
  const detail = useAssessment(id);
  const thread = useThread(id, { poll: true });
  const qs = useQuestions(id);
  const read = useMarkRead(id);
  const post = usePostMessage(id);
  const toast = useToast();
  const { tenant, me } = useSession();
  const insurer = can(tenant, 'messages.internal');
  const hash = useHash();
  const question = hashQuery(hash).get('question');
  const [pending, setPending] = useState<Pending[]>([]);
  // Where "New" goes: what was unread when the conversation opened, kept while it stays open.
  const [readBefore, setReadBefore] = useState<string | null | undefined>(undefined);
  const end = useRef<HTMLDivElement>(null);
  const newLine = useRef<HTMLLIElement>(null);
  const [atEnd, setAtEnd] = useState(true);
  const [behind, setBehind] = useState(0);

  const unread = thread.data?.unread ?? 0;
  // Set once, on the first load (React's adjust-state-while-rendering pattern).
  if (thread.data && readBefore === undefined)
    setReadBefore(thread.data.unread > 0 ? thread.data.last_read_at : null);
  // Reading the conversation reads the thread, also when someone writes while it is open.
  useEffect(() => {
    if (unread > 0 && !read.isPending) read.mutate();
  }, [unread]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(
    () => (thread.data?.messages ?? []).filter((m) => !question || m.question_key === question),
    [thread.data, question],
  );
  const mine = pending.filter((p) => !question || p.question === question);
  const questionText =
    (question && (qs.data?.questions.find((q) => q.key === question)?.definition.text ?? shown.find((m) => m.question_text)?.question_text)) || null; // prettier-ignore

  // The end of the conversation is in view: new entries scroll it along, otherwise a button says so.
  useEffect(() => {
    const el = end.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      setAtEnd(!!e?.isIntersecting);
      if (e?.isIntersecting) setBehind(0);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [thread.isPending]);

  // Opening: to the first unread entry, or the end. Then each new entry: along with it, if at the end.
  const opened = useRef(false);
  const count = shown.length + mine.length;
  const lastCount = useRef(count);
  useLayoutEffect(() => {
    if (thread.isPending || readBefore === undefined) return;
    if (!opened.current) {
      opened.current = true;
      void (newLine.current ?? end.current)?.scrollIntoView({ block: newLine.current ? 'center' : 'end' });
    } else if (count > lastCount.current) {
      if (atEnd || mine.some((p) => !p.failed))
        void end.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
      else setBehind((b) => b + count - lastCount.current);
    }
    lastCount.current = count;
  }, [count, thread.isPending, readBefore]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    void end.current?.scrollIntoView({ block: 'end' });
  }, [question]);

  const send = async (p: Pending) => {
    setPending((list) => [...list.filter((x) => x.key !== p.key), { ...p, failed: false }]);
    try {
      await post.mutateAsync({
        body: p.body,
        internal: p.internal,
        ...(p.question ? { question: p.question } : {}),
      });
      setPending((list) => list.filter((x) => x.key !== p.key));
    } catch (err) {
      setPending((list) => list.map((x) => (x.key === p.key ? { ...x, failed: true } : x)));
      toast(err instanceof Error ? err.message : 'Could not send', 'bad');
    }
  };

  if (detail.isPending || thread.isPending) return <div className="sl-pick">Loading…</div>;
  if (detail.isError || thread.isError)
    return (
      <div className="sl-pick">
        <h2>Could not load the conversation</h2>
        <p>{(detail.error ?? thread.error)?.message}</p>
      </div>
    );

  const firstNew = readBefore
    ? shown.find((m) => m.author?.id !== me?.user.id && m.created_at > readBefore)
    : undefined;
  const me_ = me?.user.id;
  const name = me?.user.name ?? me?.user.email ?? 'You';

  return (
    <>
      <header className="sl-head">
        <a className="btn btn-ghost btn-icon sl-back" href={href.messages(null)} aria-label="All conversations">
          <ArrowLeft size={18} />
        </a>
        <Avatar name={detail.data.customer_name} square />
        <div className="grow">
          <h2>{detail.data.customer_name}</h2>
          <p>
            {detail.data.title} · Everyone taking part sees this{insurer ? ', apart from internal notes' : ''}.
          </p>
        </div>
        <a className="btn btn-sm" href={href.assessment(id)}>
          <ClipboardList size={14} /> Assessment
        </a>
      </header>
      {question && (
        <div className="thread-filter">
          <MessageSquare size={14} />
          <span className="grow">
            Comments on <b>{questionText ?? question}</b>
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => navigate(href.messages(id), { replace: true })}>
            <X size={13} /> Show everything
          </button>
        </div>
      )}
      <div className="sl-scroll">
        <ol className="sl-list" aria-label="Messages">
          {shown.length === 0 && mine.length === 0 && (
            <li className="sl-start">
              <Avatar name={detail.data.customer_name} square />
              <h3>{question ? 'No comments on this question yet' : `This is the start of the conversation about ${detail.data.customer_name}`}</h3>
              <p>{question ? 'Write the first one below.' : 'Write the first message below. Everyone taking part is told.'}</p>
            </li>
          )}
          {shown.map((m, i) => {
            const prev = shown[i - 1];
            const newDay = !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
            const cont =
              !newDay && !!prev && m !== firstNew && m.kind === 'message' && prev.kind === 'message' &&
              prev.author?.id === m.author?.id && prev.internal === m.internal &&
              new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() < GROUP_MS; // prettier-ignore
            return (
              <Entry key={m.id} m={m} id={id} cont={cont} mine={m.author?.id === me_}>
                {newDay && <DayLine iso={m.created_at} />}
                {m === firstNew && (
                  <li className="sl-new" ref={newLine} role="separator">
                    <span>New</span>
                  </li>
                )}
              </Entry>
            );
          })}
          {mine.map((p, i) => {
            const before = shown.at(-1);
            const cont = i > 0
              ? mine[i - 1]!.internal === p.internal
              : !!before && before.kind === 'message' && before.author?.id === me_ && before.internal === p.internal && new Date(p.at).getTime() - new Date(before.created_at).getTime() < GROUP_MS; // prettier-ignore
            return (
              <li key={p.key} className={`msg-item message mine sending${p.failed ? ' failed' : ''}${p.internal ? ' internal' : ''}${cont ? ' cont' : ''}`}>
                {cont ? <span className="sl-gutter" /> : <Avatar name={name} />}
                <div className="sl-msg">
                  {!cont && (
                    <div className="msg-meta">
                      <b>{name}</b>
                      <time>{time(p.at)}</time>
                    </div>
                  )}
                  <div className="msg-body">{p.body}</div>
                  <div className="sl-sub">
                    {p.failed ? (
                      <>
                        Not sent.{' '}
                        <button type="button" className="linklike" onClick={() => void send(p)}>
                          Try again
                        </button>{' '}
                        <button type="button" className="linklike" onClick={() => setPending((l) => l.filter((x) => x.key !== p.key))}>
                          Discard
                        </button>
                      </>
                    ) : (
                      'Sending…'
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
        <div ref={end} className="sl-end" />
        {behind > 0 && !atEnd && (
          <button type="button" className="sl-jump" onClick={() => end.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })}>
            <ArrowDown size={14} /> {behind} new {behind === 1 ? 'message' : 'messages'}
          </button>
        )}
      </div>
      <Composer
        id={id}
        insurer={insurer}
        question={question}
        questionText={questionText}
        to={detail.data.customer_name}
        onSend={(body, internal) => void send({ key: crypto.randomUUID(), body, internal, question, failed: false, at: new Date().toISOString() })}
      />
    </>
  ); // prettier-ignore
}

/** A day's first entry is headed by the day, as Slack's dividers. */
function DayLine({ iso }: { iso: string }) {
  return (
    <li className="sl-day" role="separator">
      <span>{dayLabel(iso)}</span>
    </li>
  );
}

function Entry({
  m,
  id,
  cont,
  mine,
  children,
}: {
  m: Message;
  id: string;
  cont: boolean;
  mine: boolean;
  children?: ReactNode;
}) {
  const who = m.author?.name ?? 'CyberScore';
  if (m.kind === 'system') {
    const Icon = (m.event && EVENT_ICON[m.event]) || ShieldCheck;
    return (
      <>
        {children}
        <li className={`msg-item system${m.internal ? ' internal' : ''}`}>
          <span className="sl-gutter sl-event-ico">
            <Icon size={16} />
          </span>
          <div className="sl-msg">
            <div className="msg-meta">
              <b>{who}</b>
              {m.author && <span className="sl-tag">{SIDE[m.author.side]}</span>}
              <time dateTime={m.created_at}>{time(m.created_at)}</time>
              {m.internal && <Only />}
            </div>
            {m.question_key && <QuestionRef m={m} id={id} />}
            <div className="msg-body">{m.body}</div>
            {m.notified > 0 && <Notified n={m.notified} />}
          </div>
        </li>
      </>
    );
  }
  return (
    <>
      {children}
      <li className={`msg-item message${m.internal ? ' internal' : ''}${mine ? ' mine' : ''}${cont ? ' cont' : ''}`}>
        {cont ? (
          <time className="sl-gutter sl-hover-time" dateTime={m.created_at}>
            {time(m.created_at)}
          </time>
        ) : (
          <Avatar name={who} />
        )}
        <div className="sl-msg">
          {!cont && (
            <div className="msg-meta">
              <b>{who}</b>
              {m.author && <span className="sl-tag">{SIDE[m.author.side]}</span>}
              <time dateTime={m.created_at}>{time(m.created_at)}</time>
              {m.internal && <Only />}
            </div>
          )}
          {m.question_key && <QuestionRef m={m} id={id} />}
          <div className="msg-body">{m.body}</div>
          {m.notified > 0 && <Notified n={m.notified} />}
        </div>
      </li>
    </>
  ); // prettier-ignore
}

const Only = () => (
  <span className="sl-only">
    <Lock size={11} /> Only your team
  </span>
);

/** Who was emailed: shown on hover, as Slack keeps a message's details out of the flow. */
const Notified = ({ n }: { n: number }) => (
  <div className="sl-sub sl-notified">
    <Mail size={11} /> Emailed {n} {n === 1 ? 'person' : 'people'}
  </div>
);

function QuestionRef({ m, id }: { m: Message; id: string }) {
  return (
    <a className="msg-question" href={href.messages(id, m.question_key!)}>
      <MessageSquare size={12} /> {m.question_text ?? m.question_key}
    </a>
  );
}

/**
 * The box at the bottom: Enter sends, Shift+Enter starts a new line, and it grows with what is written.
 * The insurer can make it an internal note, which only their team sees and nobody is emailed about.
 */
function Composer({
  id,
  insurer,
  question,
  questionText,
  to,
  onSend,
}: {
  id: string;
  insurer: boolean;
  question: string | null;
  questionText: string | null;
  to: string;
  onSend: (body: string, internal: boolean) => void;
}) {
  const draftKey = `${id}:${question ?? ''}`;
  const [body, setBodyState] = useState(() => drafts.get(draftKey) ?? '');
  const [internal, setInternal] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const setBody = (v: string) => {
    setBodyState(v);
    if (v) drafts.set(draftKey, v);
    else drafts.delete(draftKey);
  };
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [body]);
  useEffect(() => {
    box.current?.focus({ preventScroll: true });
  }, [id, question]);

  const submit = () => {
    const text = body.trim();
    if (!text) return;
    onSend(text, internal);
    setBody('');
    box.current?.focus();
  };

  return (
    <form
      className={`sl-composer${internal ? ' is-internal' : ''}`}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="sr-only" htmlFor={`compose-${id}`}>
        {question ? 'Your comment' : 'Your message'}
      </label>
      <div className="sl-box">
        <textarea
          id={`compose-${id}`}
          ref={box}
          rows={1}
          maxLength={MESSAGE_MAX}
          value={body}
          placeholder={internal ? 'Write a note only your team sees' : question ? `Comment on “${questionText ?? question}”` : `Message ${to}`}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <div className="sl-box-bar">
          {insurer && (
            <label className="sl-toggle" title="Only your team sees it, and nobody is emailed">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
              <Lock size={13} /> Internal note
            </label>
          )}
          <span className="sl-hint">
            {internal ? 'Only your team sees it; nobody is emailed.' : 'Enter to send · Shift + Enter for a new line'}
          </span>
          <button type="submit" className="sl-send" aria-label={internal ? 'Add note' : 'Send'} disabled={!body.trim()}>
            <Send size={16} />
          </button>
        </div>
      </div>
    </form>
  ); // prettier-ignore
}
